import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  completePasswordReset,
  homeFor,
  inspectPasswordToken,
  type PasswordTokenInfo,
} from '../auth'
import { AnonymousShell } from '../components/AnonymousShell'
import { MIN_PASSWORD_LENGTH, tokenFromHash } from '../utils/passwordLink'
import { paths } from '../routes'

/** PUBLIC: redeem an invite or reset link — choose a password, then land
 *  signed in on the portal. The token is checked first (without being
 *  consumed) so an expired link says so before the visitor types anything. */
export function SetPassword() {
  const navigate = useNavigate()
  const [token] = useState(() => tokenFromHash(window.location.hash))
  const [info, setInfo] = useState<PasswordTokenInfo | null>(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setInfo({ valid: false })
      return
    }
    inspectPasswordToken(token)
      .then(setInfo)
      .catch((e: Error) => setError(e.message))
  }, [token])

  const mismatch = confirm.length > 0 && password !== confirm
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH
  const canSubmit = !busy && password.length >= MIN_PASSWORD_LENGTH && password === confirm

  return (
    <AnonymousShell>
      <div className="auth-card" data-testid="set-password-page">
        {!info && !error && <p>Checking your link…</p>}

        {info && !info.valid && (
          <div data-testid="set-password-invalid">
            <h1>This link has expired</h1>
            <p>
              Invite and reset links work once and expire. Request a new reset link, or ask your
              AllCode contact to resend your invite.
            </p>
            <p>
              <Link to={paths.forgotPassword()} className="button-link primary">
                Request a new link
              </Link>
            </p>
          </div>
        )}

        {info?.valid && (
          <form
            className="login-password-form"
            onSubmit={async (e) => {
              e.preventDefault()
              setError(null)
              setBusy(true)
              try {
                const user = await completePasswordReset(token, password)
                // Drop the spent token from the address bar / history.
                window.history.replaceState(null, '', window.location.pathname)
                navigate(homeFor(user), { replace: true })
              } catch (err) {
                setError((err as Error).message)
              } finally {
                setBusy(false)
              }
            }}
          >
            <h1>{info.purpose === 'invite' ? `Welcome${info.name ? `, ${info.name}` : ''}` : 'Choose a new password'}</h1>
            <p>
              {info.purpose === 'invite'
                ? "Set a password to start filling in AllCode's AI Assessment."
                : 'Pick a new password for your account.'}
              <br />
              <br />
              Signing in as <strong data-testid="set-password-email">{info.email}</strong>
            </p>
            <label>
              New password
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="set-password-new"
                required
              />
            </label>
            <label>
              Confirm password
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="set-password-confirm"
                required
              />
            </label>
            <div className="muted" style={{ fontSize: '0.8rem' }}>
              {tooShort
                ? `At least ${MIN_PASSWORD_LENGTH} characters.`
                : mismatch
                  ? "The passwords don't match."
                  : `Use at least ${MIN_PASSWORD_LENGTH} characters — a short phrase works well.`}
            </div>
            <button type="submit" className="primary" disabled={!canSubmit} data-testid="set-password-submit">
              {busy ? 'Saving…' : 'Set password & sign in'}
            </button>
          </form>
        )}

        {error && (
          <div className="notice notice--error" role="alert" style={{ marginTop: '1rem' }}>
            {error}
          </div>
        )}
      </div>
    </AnonymousShell>
  )
}
